import {
  type ConversationKind,
  type ConversationMemberRole,
  type ManagedBy,
  type PostingPolicy,
  ROLES,
  type WebhooksManagedBy,
  type Role,
  isChannelKind,
  isRoleAtLeast,
} from './enums';

/**
 * The permission matrix (D-006, PLAN §4 "Phase 0 permission matrix"). One table, used by the API
 * in route preHandlers (`requirePermission(action)`) and by the web app only to hide controls —
 * the client is never trusted.
 *
 * `permissionScope()` answers *how much* of an action a role may do:
 * - `all`  — on any target in the workspace (subject to the role-change rules below);
 * - `self` — only on themselves (guests reading members in Phase 0);
 * - `own`  — only on things they created (managers managing the invites they sent);
 * - `none` — not at all.
 * `can()` is `permissionScope() !== 'none'`.
 *
 * The cross-cutting role rules of D-006 (only an Owner grants/revokes Owner, Admins never act on an
 * Owner's role or status, the last active Owner stays, nobody changes their own role) are the
 * `check*` helpers at the bottom; the members and invites services call them.
 */

export const PERMISSION_ACTIONS = [
  'workspace.read',
  'workspace.update',
  'members.read',
  'members.read_deactivated',
  'members.update_admin_fields',
  'members.change_role',
  'members.deactivate',
  'members.reactivate',
  'members.revoke_sessions',
  'members.manage_avatar',
  'invites.create',
  'invites.manage',
  'departments.manage',
  'profile_fields.manage',
  'audit.read',
  // Phase 1 — messaging (PLAN §4 "Bootstrap, conversations and channels"). Resource rules (who
  // may rename, archive or post in one conversation) are `conversationCapabilities()` below.
  /** Create a channel; `messaging.channelCreationRoles` narrows it further (`canCreateChannel`). */
  'channels.create',
  /** The channel browser and public-channel previews. Guests only see their own conversations. */
  'channels.browse',
  /** Admin moderation of any readable channel: archive, rename, remove people, set channel managers. */
  'conversations.moderate',
  /** Making a public channel private is Owner-only (D-109). */
  'conversations.make_private',
  /** `GET/PUT /admin/default-channels`. */
  'default_channels.manage',
  /** Delete someone else's message (audited as `message.deleted`, D-046). */
  'messages.delete_any',
  /** `GET /admin/messages/:id/revisions`: deleted and pre-edit content (D-046). */
  'messages.view_deleted_content',
  // Phase 2 (PLAN §4 Phase 2 rows). Setting-dependent rights (user groups, custom emoji, a
  // channel's incoming webhooks) are the `canManage*` helpers below, not matrix cells.
  /** `/admin/bots*`: bot users and their tokens (D-072). */
  'bots.manage',
  /** `/admin/outgoing-webhooks*`. */
  'outgoing_webhooks.manage',
  /** The Owner-only private-range allowlist `integrations.webhookAllowedPrivateCidrs` (D-104). */
  'integrations.manage_private_networks',
  /** Disable or re-enable any slash command, built-in or bot (`slash_commands.enabled`). */
  'slash_commands.manage',
  /** `/admin/imports*` (Slack export import). */
  'imports.manage',
  /** `POST /admin/exports` of public data. */
  'exports.request',
  /** Exports that include private channels or DMs (SPEC §4.9: Owner only). */
  'exports.include_private',
  /** Delete someone else's custom emoji. */
  'emoji.delete_any',
  /** SSO provider settings (`auth.ssoProviders`) and `auth.passwordLoginEnabled`. */
  'sso.manage',
  // Phase 4 — tasks and projects (PLAN §4 "Later modules — Tasks and delegation", D-208). Task
  // reads and writes are resource rules (`canViewTask`, the transition tables, `canEditTaskField`).
  /** `GET/PATCH /admin/settings/tasks`, any department's `/admin/departments/:id/task-settings`. */
  'tasks.manage_settings',
  /** `/admin/delegation-guard-exceptions*` (SPEC §6.3 exceptions, audited). */
  'tasks.manage_guard_exceptions',
  /** `/admin/members/:userId/offboarding-preview` and `/offboard` (Owners only for an Owner). */
  'members.offboard',
  /** `POST /projects` (member+). Editing is the lead's or admin+'s (a resource rule). */
  'projects.create',
  // Phase 5 — PTSTDM (PLAN §4 "PTSTDM core and daily tracking", §5.1). Guests see no PTSTDM at
  // all. Everything finer than the role — `ptstdm-editor(D)` (admin+ or the head of D),
  // `report-view(U)`, `chain(U)` — is resource policy in `ptstdm-access.ts`, checked by the
  // services, because a department head or a manager in the chain can be any role.
  /** Seats, KRAs, recurring definitions, the KPI catalogue, one's own checklist, agenda and report. */
  'ptstdm.view',
  /** Integration sources, mappings, user links and uploads (`/admin/integration-sources*`). */
  'ptstdm.manage_integrations',
  /** The holiday calendar (`/admin/holidays*`) and trigger event types. */
  'ptstdm.manage_calendar',
  /** PTSTDM completeness per department (Founder's Office; `grant:cockpit` joins with Phase 4's grants). */
  'ptstdm.view_completeness',
  // Phase 6 — calls and huddles (D-648). Joining, ringing back and reading call history follow
  // conversation membership (the access policy), not the matrix; only starting a call or huddle
  // and the workspace's call settings are matrix actions.
  /** Start a call, start or join-by-creating a huddle, ring more people. Guests only answer and join. */
  'calls.start',
  /** `GET/PATCH /admin/settings/calls`: `calls.enabled`, the participant cap and the ring timeout. */
  'calls.manage_settings',
  // Wave 4 — Phases 7 to 9 (D-800 ff.). Everything finer than the role (`report-view`, `chain`,
  // department heads, the subject, four eyes, and the grants `cockpit`, `hr_compensation`,
  // `payroll_export`, `pay_approver`) is resource policy in `performance-access.ts`. The pay
  // endpoints are gated by grants, never by a matrix action: an Admin gets nothing in pay by role.
  /** Use an AI feature (summaries, catch-up, narratives, transcript extraction). Never guests. */
  'ai.use',
  /** `/admin/ai/usage`, `GET /admin/ai/budget`, `/admin/settings/ai`. */
  'ai.manage',
  /** `PUT /admin/ai/budget`: the monthly cap is Owner-only (D-061). */
  'ai.manage_budget',
  /** Appraisal forms and cycles: create, launch, calibrate, lock. */
  'appraisal.manage',
  /** `POST /admin/weekly-reviews/regenerate`. */
  'weekly_reviews.regenerate',
  /** `POST /meeting-series`: manager+ (a department head who is a member is let in by the service). */
  'meetings.create_series',
  /** `/admin/member-grants`: Owner for compensation grants, Owner or Admin for `cockpit`. */
  'member_grants.manage',
  // Wave 5 — calendar, guests, webinars, booking pages (D-1000 ff.). Who may see or change one
  // meeting, webinar or page is resource policy in the services (D-1006: no admin-sees-all for
  // calendar meetings); these are the route gates.
  /** The calendar, scheduling meetings with members and guests, answering, feeds and preferences. */
  'calendar.use',
  /** `/admin/settings/calendar`, pending invitations, orphaned series and chair reassignment. */
  'calendar.manage_settings',
  /** Create a webinar (Akshay, 11 Oct 2026: managers and above; `webinars.enabled` must be on). */
  'webinars.create',
  /** Create a personal booking page (`bookings.enabled` must be on). */
  'bookings.create_page',
  /** Create and manage team booking pages (hosts in the creator's reporting chain unless admin). */
  'bookings.manage_team_pages',
] as const;
export type PermissionAction = (typeof PERMISSION_ACTIONS)[number];

/**
 * `self` = only one's own record, `own` = records one created, `shared` = oneself and the people
 * who share a conversation with one (a guest's directory, PLAN §4 permission matrix).
 */
export type PermissionScope = 'all' | 'self' | 'own' | 'shared' | 'none';

/** Workspace settings that change the matrix. */
export interface PermissionSettings {
  /** `org.managersCanInvite` (D-006). Default false. */
  managersCanInvite?: boolean;
}

/** A matrix cell: a fixed scope, or a scope that applies only while `managersCanInvite` is on. */
type Cell = PermissionScope | { ifManagersCanInvite: Exclude<PermissionScope, 'none'> };

const ADMIN_ONLY: Record<Role, Cell> = {
  owner: 'all',
  admin: 'all',
  manager: 'none',
  member: 'none',
  guest: 'none',
};

const NON_GUESTS: Record<Role, Cell> = {
  owner: 'all',
  admin: 'all',
  manager: 'all',
  member: 'all',
  guest: 'none',
};

const MANAGER_UP: Record<Role, Cell> = {
  owner: 'all',
  admin: 'all',
  manager: 'all',
  member: 'none',
  guest: 'none',
};

const OWNER_ONLY: Record<Role, Cell> = {
  owner: 'all',
  admin: 'none',
  manager: 'none',
  member: 'none',
  guest: 'none',
};

const MATRIX: Record<PermissionAction, Record<Role, Cell>> = {
  'workspace.read': { owner: 'all', admin: 'all', manager: 'all', member: 'all', guest: 'all' },
  'workspace.update': ADMIN_ONLY,
  // Guests: self only in Phase 0; from Phase 1, people who share a conversation (`shared`).
  'members.read': { owner: 'all', admin: 'all', manager: 'all', member: 'all', guest: 'shared' },
  'members.read_deactivated': ADMIN_ONLY,
  'members.update_admin_fields': ADMIN_ONLY,
  // Admins: never to or from owner, never on an owner (checkRoleChange).
  'members.change_role': ADMIN_ONLY,
  // Admins: not owners (checkDeactivation / checkOwnerTarget).
  'members.deactivate': ADMIN_ONLY,
  'members.reactivate': ADMIN_ONLY,
  'members.revoke_sessions': ADMIN_ONLY,
  'members.manage_avatar': ADMIN_ONLY,
  // Managers: member and guest roles only (grantableInviteRoles), only when managersCanInvite.
  'invites.create': {
    owner: 'all',
    admin: 'all',
    manager: { ifManagersCanInvite: 'all' },
    member: 'none',
    guest: 'none',
  },
  // Managers: only invites they sent, only when managersCanInvite.
  'invites.manage': {
    owner: 'all',
    admin: 'all',
    manager: { ifManagersCanInvite: 'own' },
    member: 'none',
    guest: 'none',
  },
  'departments.manage': ADMIN_ONLY,
  'profile_fields.manage': ADMIN_ONLY,
  'audit.read': ADMIN_ONLY,
  'channels.create': NON_GUESTS,
  'channels.browse': NON_GUESTS,
  'conversations.moderate': ADMIN_ONLY,
  'conversations.make_private': OWNER_ONLY,
  'default_channels.manage': ADMIN_ONLY,
  'messages.delete_any': ADMIN_ONLY,
  'messages.view_deleted_content': OWNER_ONLY,
  'bots.manage': ADMIN_ONLY,
  'outgoing_webhooks.manage': ADMIN_ONLY,
  'integrations.manage_private_networks': OWNER_ONLY,
  'slash_commands.manage': ADMIN_ONLY,
  'imports.manage': ADMIN_ONLY,
  'exports.request': ADMIN_ONLY,
  'exports.include_private': OWNER_ONLY,
  'emoji.delete_any': ADMIN_ONLY,
  'sso.manage': ADMIN_ONLY,
  'tasks.manage_settings': ADMIN_ONLY,
  'tasks.manage_guard_exceptions': ADMIN_ONLY,
  // Admins: not owners (checkOwnerTarget).
  'members.offboard': ADMIN_ONLY,
  'projects.create': NON_GUESTS,
  'ptstdm.view': NON_GUESTS,
  'ptstdm.manage_integrations': ADMIN_ONLY,
  'ptstdm.manage_calendar': ADMIN_ONLY,
  'ptstdm.view_completeness': ADMIN_ONLY,
  'calls.start': NON_GUESTS,
  'calls.manage_settings': ADMIN_ONLY,
  'ai.use': NON_GUESTS,
  'ai.manage': ADMIN_ONLY,
  'ai.manage_budget': OWNER_ONLY,
  'appraisal.manage': ADMIN_ONLY,
  'weekly_reviews.regenerate': ADMIN_ONLY,
  'meetings.create_series': MANAGER_UP,
  'member_grants.manage': ADMIN_ONLY,
  'calendar.use': NON_GUESTS,
  'calendar.manage_settings': ADMIN_ONLY,
  'webinars.create': MANAGER_UP,
  'bookings.create_page': NON_GUESTS,
  'bookings.manage_team_pages': MANAGER_UP,
};

export function isPermissionAction(value: unknown): value is PermissionAction {
  return typeof value === 'string' && Object.hasOwn(MATRIX, value);
}

/** How much of `action` the role may perform. */
export function permissionScope(
  role: Role,
  action: PermissionAction,
  settings: PermissionSettings = {},
): PermissionScope {
  const cell = MATRIX[action][role];
  if (typeof cell === 'string') return cell;
  return settings.managersCanInvite ? cell.ifManagersCanInvite : 'none';
}

/** True when the role may perform the action at all (see `permissionScope` for the extent). */
export function can(
  role: Role,
  action: PermissionAction,
  settings: PermissionSettings = {},
): boolean {
  return permissionScope(role, action, settings) !== 'none';
}

/** Every action the role may perform, in matrix order (`MeDto.permissions`). */
export function permissionsFor(role: Role, settings: PermissionSettings = {}): PermissionAction[] {
  return PERMISSION_ACTIONS.filter((action) => can(role, action, settings));
}

// ---------------------------------------------------------------- role rules (D-006)

/** Why a role rule refused; each is an API error code in `@orbit/shared/errors`. */
export type RoleRuleViolation =
  | 'forbidden'
  | 'cannot_change_own_role'
  | 'cannot_deactivate_self'
  | 'owner_required'
  | 'last_owner'
  | 'role_not_grantable';

export type RoleRuleResult =
  { ok: true } | { ok: false; code: RoleRuleViolation; permission?: PermissionAction };

const OK: RoleRuleResult = Object.freeze({ ok: true });
const deny = (code: RoleRuleViolation, permission?: PermissionAction): RoleRuleResult =>
  permission ? { ok: false, code, permission } : { ok: false, code };

/**
 * Roles the actor may put on an invite: Owners any role, Admins admin/manager/member/guest,
 * Managers (only with `managersCanInvite`) member/guest, everyone else none.
 */
export function grantableInviteRoles(actorRole: Role, settings: PermissionSettings = {}): Role[] {
  if (!can(actorRole, 'invites.create', settings)) return [];
  switch (actorRole) {
    case 'owner':
      return [...ROLES];
    case 'admin':
      return ['admin', 'manager', 'member', 'guest'];
    case 'manager':
      return ['member', 'guest'];
    default:
      return [];
  }
}

/** Whether the actor may invite someone with `inviteRole`. */
export function checkInviteRole(
  actorRole: Role,
  inviteRole: Role,
  settings: PermissionSettings = {},
): RoleRuleResult {
  if (!can(actorRole, 'invites.create', settings)) return deny('forbidden', 'invites.create');
  return grantableInviteRoles(actorRole, settings).includes(inviteRole)
    ? OK
    : deny('role_not_grantable');
}

/**
 * Rank-only answer to "may this actor move someone from `targetCurrentRole` to `newRole`?":
 * needs `members.change_role`, and anything that touches Owner (granting it, revoking it, or
 * changing an Owner) needs the actor to be an Owner. Does not know about self-changes or the
 * last Owner — use {@link checkRoleChange} for the full rule set.
 */
export function canAssignRole(actorRole: Role, targetCurrentRole: Role, newRole: Role): boolean {
  if (!can(actorRole, 'members.change_role')) return false;
  if ((targetCurrentRole === 'owner' || newRole === 'owner') && actorRole !== 'owner') return false;
  return true;
}

export interface RoleChangeInput {
  actorRole: Role;
  /** The actor is the target (nobody changes their own role). */
  isSelf: boolean;
  targetCurrentRole: Role;
  newRole: Role;
  /** Active Owners in the workspace, including the target if they are one. */
  activeOwnerCount: number;
}

/** Full D-006 role-change rules, in the order the API reports them. */
export function checkRoleChange(input: RoleChangeInput): RoleRuleResult {
  const { actorRole, isSelf, targetCurrentRole, newRole, activeOwnerCount } = input;
  if (!can(actorRole, 'members.change_role')) return deny('forbidden', 'members.change_role');
  if (isSelf) return deny('cannot_change_own_role');
  if (!canAssignRole(actorRole, targetCurrentRole, newRole)) return deny('owner_required');
  if (targetCurrentRole === 'owner' && newRole !== 'owner' && activeOwnerCount <= 1) {
    return deny('last_owner');
  }
  return OK;
}

export interface DeactivationInput {
  actorRole: Role;
  isSelf: boolean;
  targetRole: Role;
  /** Whether the target is currently active (deactivating an inactive member is a no-op). */
  targetActive: boolean;
  activeOwnerCount: number;
}

/** D-006/D-023 deactivation rules: not yourself, Owners only by Owners, never the last Owner. */
export function checkDeactivation(input: DeactivationInput): RoleRuleResult {
  const { actorRole, isSelf, targetRole, targetActive, activeOwnerCount } = input;
  if (!can(actorRole, 'members.deactivate')) return deny('forbidden', 'members.deactivate');
  if (isSelf) return deny('cannot_deactivate_self');
  if (targetRole === 'owner' && actorRole !== 'owner') return deny('owner_required');
  if (targetRole === 'owner' && targetActive && activeOwnerCount <= 1) return deny('last_owner');
  return OK;
}

/**
 * Actions an Admin may take on anyone except an Owner (reactivate, revoke all sessions): the
 * actor needs the permission, and an Owner target needs an Owner actor.
 */
export function checkOwnerTarget(
  actorRole: Role,
  targetRole: Role,
  action: Extract<
    PermissionAction,
    'members.reactivate' | 'members.revoke_sessions' | 'members.offboard'
  >,
): RoleRuleResult {
  if (!can(actorRole, action)) return deny('forbidden', action);
  if (targetRole === 'owner' && actorRole !== 'owner') return deny('owner_required');
  return OK;
}

// ---------------------------------------------------------------- conversation rules (Phase 1)

/**
 * Per-conversation rules (PLAN §4 messaging tables, §5.1 authorization model, D-006, D-054,
 * D-109). Pure: the API's conversation access policy (`apps/api/src/services/conversation-access.ts`)
 * loads the facts and is the only authority; the web app uses the same function only to hide
 * controls. Visibility answers 404 (`canRead` false), a visible but refused action 403.
 */

/** Who is asking. Role and guest scope come from the request context, never from the client. */
export interface ConversationActor {
  userId: string;
  role: Role;
  /**
   * A bot token (D-180). Bots have role `member` but read only conversations they belong to: no
   * public-channel previews (PLAN §4 "Bots and bot tokens").
   */
  isBot?: boolean;
}

/** The conversation row facts the rules need. */
export interface ConversationFacts {
  kind: ConversationKind;
  isGeneral: boolean;
  createdByUserId: string;
  archivedAt: Date | string | null;
  deletedAt: Date | string | null;
  postingPolicy: PostingPolicy;
}

/** The actor's active membership (`left_at IS NULL`), or null when they are not a member. */
export interface MembershipFacts {
  role: ConversationMemberRole;
}

export interface ConversationCapabilities {
  isMember: boolean;
  /** Member with `conversation_members.role = 'channel_manager'`. */
  isChannelManager: boolean;
  /** Read messages, members and details (otherwise the API answers 404). */
  canRead: boolean;
  canPost: boolean;
  /**
   * Reply INSIDE a thread, where posting to the room itself may be restricted (D-1214).
   *
   * An announcement channel is one where one person talks and everybody else listens — but
   * "listens" was taken literally: `canPost` gates every message, replies included, so the
   * audience could not ask a question about the announcement they had just been given. That is
   * not a quieter channel, it is a channel with the conversation moved somewhere else.
   *
   * A thread is the answer the product already has: it is off to one side, it does not touch the
   * channel's own flow, and it is where a question about one message belongs. So anyone who may
   * react here may reply in a thread here.
   *
   * It does NOT carry `alsoSentToChannel`: a reply sent back to the room is a message in the
   * room, and that is exactly what the policy restricts. `canPost` governs that one.
   */
  canReplyInThread: boolean;
  canReact: boolean;
  /**
   * Start a call, a huddle or a meeting here (D-1212).
   *
   * The same answer as `canPost`, deliberately: a call, a huddle and a scheduled meeting each
   * announce themselves in the conversation, and in an announcement channel somebody who may not
   * write a line should not be able to put a ringing room in front of everyone instead. Joining one
   * that is already running is a different question and is not this.
   */
  canStartCall: boolean;
  canJoin: boolean;
  canLeave: boolean;
  canRename: boolean;
  /** Topic and description. */
  canEditTopic: boolean;
  canArchive: boolean;
  canUnarchive: boolean;
  canAddMembers: boolean;
  /** Adding a guest to a channel needs admin+ as well (PLAN §4 `POST /conversations/:id/members`). */
  canAddGuests: boolean;
  canRemoveMembers: boolean;
  canChangeMemberRoles: boolean;
  /** Private → public: the Owner, or a channel manager who is a member (D-109). */
  canConvertToPublic: boolean;
  /** Public → private: the Owner only; never #general. */
  canConvertToPrivate: boolean;
  /** Delete other people's messages (admin+, audited). */
  canDeleteOthersMessages: boolean;
  /** Per-member settings: notify level, mute, star (`PATCH /conversations/:id/member-state`). */
  canUpdateMemberState: boolean;
}

const NO_CAPABILITIES: ConversationCapabilities = Object.freeze({
  isMember: false,
  isChannelManager: false,
  canRead: false,
  canPost: false,
  canReplyInThread: false,
  canReact: false,
  canStartCall: false,
  canJoin: false,
  canLeave: false,
  canRename: false,
  canEditTopic: false,
  canArchive: false,
  canUnarchive: false,
  canAddMembers: false,
  canAddGuests: false,
  canRemoveMembers: false,
  canChangeMemberRoles: false,
  canConvertToPublic: false,
  canConvertToPrivate: false,
  canDeleteOthersMessages: false,
  canUpdateMemberState: false,
});

/**
 * What `actor` may do in one conversation.
 * - Public channels are readable by every non-guest (preview without joining); everything else,
 *   and everything for guests, needs an active membership (D-006).
 * - Task comment streams (`kind = 'task'`) are governed by task visibility (D-054), which arrives in
 *   Phase 4; until then nobody can read them through the messaging rules.
 * - Deleted conversations are invisible to everyone; archived channels are read-only.
 */
export function conversationCapabilities(
  actor: ConversationActor,
  conversation: ConversationFacts,
  membership: MembershipFacts | null,
): ConversationCapabilities {
  if (conversation.deletedAt || conversation.kind === 'task') return NO_CAPABILITIES;
  const isGuest = actor.role === 'guest';
  const isBot = actor.isBot === true;
  const isAdmin = isRoleAtLeast(actor.role, 'admin');
  const isOwner = actor.role === 'owner';
  const isMember = membership !== null;
  const isChannelManager = membership?.role === 'channel_manager';
  const isChannel = isChannelKind(conversation.kind);
  const isPublic = conversation.kind === 'public_channel';
  const isCreator = conversation.createdByUserId === actor.userId;
  const archived = conversation.archivedAt !== null;

  const canRead = isMember || (isPublic && !isGuest && !isBot);
  if (!canRead) return NO_CAPABILITIES;

  const canPost =
    isMember &&
    !archived &&
    (conversation.postingPolicy === 'everyone' || isAdmin || isChannelManager);
  const canAddMembers = isChannel && isMember && !archived && !isGuest;
  return {
    isMember,
    isChannelManager,
    canRead,
    canPost,
    // D-1214: whoever may react here may reply in a thread here, posting policy or not.
    canReplyInThread: isMember && !archived,
    canReact: isMember && !archived,
    // D-1212: whoever may write here may ring here. See the interface for why they are one rule.
    canStartCall: canPost,
    canJoin: isPublic && !isMember && !isGuest && !archived,
    canLeave: isChannel && isMember && !conversation.isGeneral,
    canRename: isChannel && !archived && (isCreator || isAdmin || isChannelManager),
    canEditTopic: isChannel && isMember && !archived,
    canArchive: isChannel && !conversation.isGeneral && !archived && (isCreator || isAdmin),
    canUnarchive: isChannel && archived && (isCreator || isAdmin),
    canAddMembers,
    canAddGuests: canAddMembers && isAdmin,
    canRemoveMembers:
      isChannel &&
      !conversation.isGeneral &&
      (isAdmin || isChannelManager || (conversation.kind === 'private_channel' && isCreator)),
    canChangeMemberRoles: isChannel && !isGuest && (isAdmin || isChannelManager),
    canConvertToPublic:
      conversation.kind === 'private_channel' &&
      isMember &&
      !archived &&
      (isOwner || isChannelManager),
    canConvertToPrivate: isPublic && !conversation.isGeneral && !archived && isOwner,
    /*
     * Moderation, and moderation is a CHANNEL idea (D-1175). It was `isAdmin` alone — the one
     * capability in this list without the `isChannel` guard every other moderation right has —
     * so an Admin could delete the other person's messages inside a one-to-one DM, in a
     * conversation they are not part of and nobody asked them into. A channel is a shared room
     * somebody has to keep in order; a DM is two people talking.
     */
    canDeleteOthersMessages: isChannel && isAdmin,
    canUpdateMemberState: isMember,
  };
}

/** Channel creation: never guests, and only roles listed in `messaging.channelCreationRoles`. */
export function canCreateChannel(role: Role, channelCreationRoles: readonly string[]): boolean {
  return can(role, 'channels.create') && channelCreationRoles.includes(role);
}

// ---------------------------------------------------------------- setting-dependent rights (Phase 2)

/**
 * User groups (`messaging.userGroupsManagedBy`): `admins` (default) = owner and admin, `managers`
 * adds managers, `everyone` = every non-guest. Guests never manage groups.
 */
export function canManageUserGroups(role: Role, managedBy: ManagedBy): boolean {
  if (role === 'guest') return false;
  if (managedBy === 'everyone') return true;
  if (managedBy === 'managers') return isRoleAtLeast(role, 'manager');
  return isRoleAtLeast(role, 'admin');
}

/** Custom emoji uploads (`messaging.emojiUploadBy`), with the same levels as user groups. */
export function canUploadCustomEmoji(role: Role, uploadBy: ManagedBy): boolean {
  return canManageUserGroups(role, uploadBy);
}

/**
 * A channel's incoming webhooks (`integrations.webhooksManagedBy`): admins always; with
 * `channel_members`, any non-guest member of the channel too.
 */
export function canManageIncomingWebhooks(
  role: Role,
  managedBy: WebhooksManagedBy,
  isChannelMember: boolean,
): boolean {
  if (isRoleAtLeast(role, 'admin')) return true;
  return managedBy === 'channel_members' && isChannelMember && role !== 'guest';
}
